import React, { FC, ReactNode, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Icon } from '@packages/ui/shared';
import { colors } from '@packages/core/styles';

export type FilterOption = {
    value: string;
    label: string;
    /** Rendered before the label in the menu, e.g. the publication type icon. */
    adornment?: ReactNode;
};

type Props = {
    /** Shown when nothing is selected, and used as the control's accessible name unless `ariaLabel` is set. */
    placeholder: string;
    /** The accessible name when a visible label sits above the control and the placeholder is only a hint. */
    ariaLabel?: string;
    options: FilterOption[];
    selected: string[];
    multiple?: boolean;
    /** Adds a filter box inside the menu. Worth it for long lists such as carlines. */
    searchable?: boolean;
    /** A number is pixels. A string such as '100%' lets the control fill a column. */
    width: number | string;
    /** Greys the control out, for a list that is waiting on the filter above it. */
    disabled?: boolean;
    hint?: string;
    onChange: (selected: string[]) => void;
};

/** The thin blue scrollbar the rest of the app uses on its scrollable panes. */
const appScrollbar = {
    scrollbarWidth: 'thin',
    scrollbarColor: `${colors.blueTwo} ${colors.grayTwo}`,
    '&::-webkit-scrollbar': {
        width: 6,
        height: 6,
    },
    '&::-webkit-scrollbar-track': {
        backgroundColor: colors.grayTwo,
        borderRadius: 3,
    },
    '&::-webkit-scrollbar-thumb': {
        backgroundColor: colors.blueTwo,
        borderRadius: 3,
        '&:hover': {
            backgroundColor: colors.blueOne,
        },
    },
    '&::-webkit-scrollbar-button': {
        display: 'none',
    },
} as const;

const useStyles = createUseStyles({
    container: {
        position: 'relative',
        flex: '0 0 auto',
    },
    /** Matches the keyword search box: 40px tall, 3px radius, 1px grayFive border. */
    trigger: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        width: '100%',
        height: 40,
        padding: '0 36px 0 12px',
        border: `1px solid ${colors.grayFive}`,
        borderRadius: 3,
        backgroundColor: colors.white,
        color: colors.textDarkPrimary,
        fontSize: 17,
        lineHeight: '24px',
        textAlign: 'left',
        cursor: 'pointer',
        '&:focus-visible': {
            outline: 'none',
            border: `2px solid ${colors.blueOne}`,
            padding: '0 35px 0 11px',
        },
    },
    triggerOpen: {
        border: `2px solid ${colors.blueOne}`,
        padding: '0 35px 0 11px',
    },
    triggerDisabled: {
        backgroundColor: colors.grayTwo,
        borderColor: colors.grayFour,
        color: colors.grayFive,
        cursor: 'not-allowed',
    },
    label: {
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    placeholder: {
        color: colors.grayFive,
    },
    /**
     * The chip the global search bar uses for a selected filter. Deliberately a block box:
     * `display: inline-flex` makes `text-overflow` a no-op, so the label was being chopped
     * mid-word with no ellipsis.
     */
    chip: {
        display: 'inline-block',
        flex: '0 1 auto',
        minWidth: 0,
        maxWidth: '100%',
        padding: '2px 6px',
        borderRadius: 4,
        border: `1px solid ${colors.blueFour}`,
        backgroundColor: colors.blueFour,
        color: colors.blueOne,
        fontSize: 14,
        fontWeight: 600,
        lineHeight: '18px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    /**
     * Never shrinks and never shares an ellipsis with the chip beside it. Keeping the count
     * inside the truncated label is what hid it behind a long publication type.
     */
    chipCount: {
        flex: '0 0 auto',
    },
    /** Exactly one of these ever renders, so the control never shows two icons. */
    endIcon: {
        position: 'absolute',
        right: 10,
        top: 8,
        display: 'flex',
        alignItems: 'center',
    },
    endIconStatic: {
        pointerEvents: 'none',
    },
    endIconClickable: {
        cursor: 'pointer',
    },
    menu: {
        position: 'absolute',
        top: 'calc(100% + 4px)',
        left: 0,
        zIndex: 1000,
        width: 'max-content',
        minWidth: '100%',
        maxWidth: 520,
        backgroundColor: colors.white,
        border: `1px solid ${colors.grayThree}`,
        borderRadius: 4,
        boxShadow: '0 6px 16px rgba(7, 20, 36, 0.14)',
        overflow: 'hidden',
    },
    menuSearch: {
        width: '100%',
        height: 36,
        padding: '0 12px',
        border: 'none',
        borderBottom: `1px solid ${colors.grayThree}`,
        fontSize: 15,
        outline: 'none',
    },
    menuList: {
        maxHeight: 300,
        overflowY: 'auto',
        overflowX: 'auto',
        ...appScrollbar,
    },
    menuItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '9px 14px',
        margin: 0,
        width: '100%',
        border: 'none',
        backgroundColor: 'transparent',
        fontSize: 15,
        fontWeight: 400,
        lineHeight: '20px',
        color: colors.textDarkPrimary,
        whiteSpace: 'nowrap',
        textAlign: 'left',
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: colors.blueFour,
        },
    },
    /**
     * A menu row is a flex line at the container's width, and the label text cannot shrink
     * below its content. Without this the browser takes the overflow out of the only
     * shrinkable child, the icon, which silently collapsed it to zero width on every long
     * publication type.
     */
    menuItemFixed: {
        flex: '0 0 auto',
        display: 'flex',
        alignItems: 'center',
    },
    menuItemSelected: {
        color: colors.blueOne,
        fontWeight: 500,
    },
    emptyMenu: {
        padding: '12px 14px',
        fontSize: 15,
        color: colors.grayFive,
        whiteSpace: 'nowrap',
    },
});

export const StisFilterDropdown: FC<Props> = ({
    placeholder,
    ariaLabel,
    options,
    selected,
    multiple = false,
    searchable = false,
    width,
    disabled = false,
    hint,
    onChange,
}) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const controlId = useId();

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Escape' && isOpen) {
            setIsOpen(false);
            containerRef.current?.querySelector('button')?.focus();
        }
    };

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        const closeOnOutsideClick = (event: MouseEvent) => {
            if (!containerRef.current?.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', closeOnOutsideClick);
        return () => document.removeEventListener('mousedown', closeOnOutsideClick);
    }, [isOpen]);

    useEffect(() => {
        if (disabled) {
            setIsOpen(false);
        }
    }, [disabled]);

    // Reopening should start from the full list rather than the last search.
    useEffect(() => {
        if (!isOpen) {
            setQuery('');
        }
    }, [isOpen]);

    const selectedLabels = useMemo(
        () => options.filter((option) => selected.includes(option.value)).map((option) => option.label),
        [options, selected]
    );

    const visibleOptions = useMemo(() => {
        if (!searchable || query.trim().length === 0) {
            return options;
        }
        const needle = query.trim().toLowerCase();
        return options.filter((option) => option.label.toLowerCase().includes(needle));
    }, [options, query, searchable]);

    const handleSelect = (value: string) => {
        if (!multiple) {
            onChange([value]);
            setIsOpen(false);
            return;
        }
        onChange(selected.includes(value) ? selected.filter((item) => item !== value) : [...selected, value]);
        // The typed text lingering after a pick was the original complaint about the old
        // typeahead. Clearing it puts the full list back so the next pick is one click away.
        setQuery('');
    };

    const hasSelection = selected.length > 0;
    const triggerClassName = `${classes.trigger} ${isOpen ? classes.triggerOpen : ''} ${
        disabled ? classes.triggerDisabled : ''
    }`;

    const labelContent = !hasSelection ? (
        <span className={`${classes.label} ${classes.placeholder}`}>{placeholder}</span>
    ) : (
        <>
            <span className={classes.chip}>{selectedLabels[0]}</span>
            {selectedLabels.length > 1 && (
                <span className={`${classes.chip} ${classes.chipCount}`}>+ {selectedLabels.length - 1}</span>
            )}
        </>
    );

    return (
        <div className={classes.container} style={{ width }} ref={containerRef} title={hint} onKeyDown={handleKeyDown}>
            {selectedLabels.length > 1 ? (
                <OverlayTrigger
                    placement="bottom"
                    overlay={<Tooltip id={`${controlId}-selection`}>{selectedLabels.join(', ')}</Tooltip>}
                >
                    <button
                        type="button"
                        className={triggerClassName}
                        disabled={disabled}
                        onClick={() => setIsOpen((previous) => !previous)}
                        aria-expanded={isOpen}
                        aria-haspopup={multiple ? 'true' : 'listbox'}
                        aria-label={ariaLabel ?? placeholder}
                    >
                        {labelContent}
                    </button>
                </OverlayTrigger>
            ) : (
                <button
                    type="button"
                    className={triggerClassName}
                    disabled={disabled}
                    onClick={() => setIsOpen((previous) => !previous)}
                    aria-expanded={isOpen}
                    aria-haspopup={multiple ? 'true' : 'listbox'}
                    aria-label={ariaLabel ?? placeholder}
                >
                    {labelContent}
                </button>
            )}

            <div
                className={`${classes.endIcon} ${
                    hasSelection && !disabled ? classes.endIconClickable : classes.endIconStatic
                }`}
            >
                {hasSelection && !disabled ? (
                    <Icon
                        name="x-close"
                        size={20}
                        onPress={() => {
                            setIsOpen(false);
                            onChange([]);
                        }}
                    />
                ) : (
                    <Icon name={isOpen ? 'chevron-up' : 'chevron-down'} size={20} />
                )}
            </div>

            {isOpen && !disabled && (
                <div
                    className={classes.menu}
                    role={multiple ? 'group' : 'listbox'}
                    aria-label={ariaLabel ?? placeholder}
                >
                    {searchable && (
                        <input
                            className={classes.menuSearch}
                            value={query}
                            autoFocus
                            placeholder={t('publications:quickFilters.searchOptions', 'Search')}
                            onChange={(event) => setQuery(event.target.value)}
                        />
                    )}
                    <div className={classes.menuList}>
                        {visibleOptions.length === 0 && (
                            <div className={classes.emptyMenu}>
                                {t('publications:quickFilters.noOptions', 'No matches')}
                            </div>
                        )}
                        {visibleOptions.map((option, index) => (
                            <OverlayTrigger
                                key={option.value}
                                placement="right"
                                overlay={<Tooltip id={`${controlId}-option-${index}`}>{option.label}</Tooltip>}
                            >
                                {multiple ? (
                                    <label className={classes.menuItem}>
                                        <input
                                            type="checkbox"
                                            className={classes.menuItemFixed}
                                            checked={selected.includes(option.value)}
                                            onChange={() => handleSelect(option.value)}
                                        />
                                        {option.adornment && (
                                            <span className={classes.menuItemFixed}>{option.adornment}</span>
                                        )}
                                        <span className={classes.menuItemFixed}>{option.label}</span>
                                    </label>
                                ) : (
                                    <button
                                        type="button"
                                        role="option"
                                        aria-selected={selected.includes(option.value)}
                                        className={`${classes.menuItem} ${
                                            selected.includes(option.value) ? classes.menuItemSelected : ''
                                        }`}
                                        onClick={() => handleSelect(option.value)}
                                    >
                                        {option.adornment && (
                                            <span className={classes.menuItemFixed}>{option.adornment}</span>
                                        )}
                                        <span className={classes.menuItemFixed}>{option.label}</span>
                                    </button>
                                )}
                            </OverlayTrigger>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};
